import { Field as BaseField } from "@base-ui/react/field";
import clsx from "clsx";
import type { ComponentProps, ReactNode, Ref } from "react";
import { FieldFooter, FieldLabel, type FieldLabelProps } from "../Field/FieldParts";
import styles from "../Field/Field.module.css";

export interface TextFieldProps
  extends
    FieldLabelProps,
    Omit<ComponentProps<"input">, "className" | "style" | "size" | "prefix" | "children" | "ref"> {
  /** Text or an icon before the value (a currency symbol, a search icon). */
  prefix?: ReactNode;
  /** A unit after the value ("g", "%", "mm"). */
  suffix?: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Monospace with wide tracking, for codes and CAS numbers (also disables spellcheck). */
  mono?: boolean;
  onValueChange?: (value: string) => void;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
}

/** A labelled single-line text input (Base UI Field). Values stay strings. */
export function TextField({
  label,
  hideLabel,
  description,
  error,
  optional,
  prefix,
  suffix,
  size = "md",
  mono = false,
  disabled,
  className,
  inputRef,
  onValueChange,
  name,
  ...inputProps
}: TextFieldProps) {
  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      className={clsx(styles.field, className)}
    >
      <FieldLabel label={label} hideLabel={hideLabel} optional={optional} />
      <div className={styles.control} data-size={size}>
        {prefix ? <span className={styles.adornment}>{prefix}</span> : null}
        <BaseField.Control
          {...inputProps}
          ref={inputRef}
          name={name}
          className={styles.input}
          data-mono={mono || undefined}
          spellCheck={mono ? false : inputProps.spellCheck}
          autoCapitalize={mono ? "off" : inputProps.autoCapitalize}
          onValueChange={onValueChange ? (value) => onValueChange(value) : undefined}
        />
        {suffix ? <span className={styles.adornment}>{suffix}</span> : null}
      </div>
      <FieldFooter description={description} error={error} />
    </BaseField.Root>
  );
}
